/**
 * Toast presentation queue: max one visible toast; durable events not overwritten.
 * Interceptor and transport must not call this — presentation owners only.
 */

import toast from "react-hot-toast";
import {
  SIMPLE_SUCCESS_TOAST_MS,
  createClientOperationId,
} from "./types";
import { getOperationCoordinator } from "./operationCoordinator";

/** @typedef {'success' | 'error' | 'loading' | 'blank' | 'custom'} ToastKind */

/**
 * @typedef {object} QueuedToast
 * @property {string} id
 * @property {ToastKind} kind
 * @property {string|import('react').ReactNode} message
 * @property {object} [options]
 * @property {string} [clientOperationId]
 * @property {string} [stepKey]
 * @property {string} [attemptId]
 * @property {string} [eventKey]
 * @property {'low' | 'normal' | 'high' | 'critical'} [priority]
 * @property {boolean} [persistent]
 */

const DEFAULT_PRIORITY = {
  error: "high",
  loading: "normal",
  success: "low",
  blank: "normal",
  custom: "normal",
};

const PRIORITY_RANK = { low: 1, normal: 2, high: 3, critical: 4 };

let visibleId = null;
/** @type {QueuedToast[]} */
const queue = [];
/** @type {Map<string, string>} dedupeKey -> toast id (queued or visible) */
const dedupeIndex = new Map();
const listeners = new Set();

/**
 * After logout/session teardown, block presentation until the next login
 * so in-flight promise callbacks cannot resurrect prior-session toasts.
 */
let presentationSuspended = false;
/**
 * Bumped on clearAllToasts so a promise that started before teardown cannot
 * enqueue after resumeSessionPresentation (login before settle).
 */
let sessionGeneration = 0;

export function isPresentationSuspended() {
  return presentationSuspended;
}

/** Current toast-session generation (capture before async work; compare after). */
export function getToastSessionGeneration() {
  return sessionGeneration;
}

/** Re-enable toast presentation after a successful login/bootstrap. */
export function resumeSessionPresentation() {
  presentationSuspended = false;
}

function notify() {
  listeners.forEach((fn) => {
    try {
      fn(getSnapshot());
    } catch (_) {
      /* ignore */
    }
  });
}

export function getSnapshot() {
  return {
    visibleId,
    queueLength: queue.length,
    queue: queue.map((q) => ({ ...q })),
    sessionGeneration,
    presentationSuspended,
  };
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function dedupeKeyFor(entry) {
  if (entry.clientOperationId || entry.stepKey || entry.eventKey) {
    return [
      entry.clientOperationId || "",
      entry.stepKey || "",
      entry.attemptId || "",
      entry.eventKey || entry.kind,
    ].join("|");
  }
  return null;
}

/**
 * Whether global toast UI should suppress because a dialog owns the action.
 * @param {string} [clientOperationId]
 */
export function shouldSuppressToastForOperation(clientOperationId) {
  const coord = getOperationCoordinator();
  if (coord.hasActiveDialogOwner()) {
    if (!clientOperationId) return true;
    return coord.isDialogOwned(clientOperationId);
  }
  return false;
}

function resolveDuration(kind, options = {}) {
  if (options.duration != null) return options.duration;
  if (options.persistent || kind === "error") return Infinity;
  if (kind === "success") return SIMPLE_SUCCESS_TOAST_MS;
  if (kind === "loading") return Infinity;
  return SIMPLE_SUCCESS_TOAST_MS;
}

function showNative(entry) {
  const duration = resolveDuration(entry.kind, entry.options);
  const opts = {
    id: entry.id,
    duration,
    ...entry.options,
  };
  delete opts.persistent;
  delete opts.clientOperationId;
  delete opts.stepKey;
  delete opts.attemptId;
  delete opts.eventKey;
  delete opts.priority;
  delete opts.sessionGeneration;

  const pauseable = {
    ...opts,
    // react-hot-toast pauses on hover by default for duration timers
  };

  switch (entry.kind) {
    case "success":
      return toast.success(entry.message, pauseable);
    case "error":
      return toast.error(entry.message, pauseable);
    case "loading":
      return toast.loading(entry.message, pauseable);
    case "blank":
      return toast(entry.message, pauseable);
    default:
      return toast.custom(entry.message, pauseable);
  }
}

function pump() {
  if (visibleId) return;
  if (queue.length === 0) {
    notify();
    return;
  }
  queue.sort(
    (a, b) =>
      (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0)
  );
  const next = queue.shift();
  visibleId = next.id;
  showNative(next);
  notify();
}

/**
 * Enqueue or update a toast. Max one visible at a time.
 * @param {ToastKind} kind
 * @param {string|import('react').ReactNode} message
 * @param {object} [options]
 */
export function enqueueToast(kind, message, options = {}) {
  // Stale async path captured a pre-clear generation — drop even after resume.
  if (
    options.sessionGeneration != null &&
    options.sessionGeneration !== sessionGeneration
  ) {
    return null;
  }

  const id =
    options.id ||
    createClientOperationId({
      operationKey: `toast-${kind}`,
      attemptId: options.attemptId,
    });

  if (presentationSuspended) {
    return id;
  }

  const clientOperationId = options.clientOperationId;
  const deferForDialog =
    options.respectDialogOwner !== false &&
    shouldSuppressToastForOperation(clientOperationId);

  /** @type {QueuedToast} */
  const entry = {
    id,
    kind,
    message,
    options,
    clientOperationId,
    stepKey: options.stepKey,
    attemptId: options.attemptId,
    eventKey: options.eventKey,
    priority: options.priority || DEFAULT_PRIORITY[kind] || "normal",
    persistent: Boolean(options.persistent) || kind === "error",
  };

  const key = dedupeKeyFor(entry);
  if (key && dedupeIndex.has(key)) {
    const existingId = dedupeIndex.get(key);
    if (!deferForDialog && existingId === visibleId) {
      showNative(entry);
      return existingId;
    }
    const qi = queue.findIndex((q) => q.id === existingId);
    if (qi >= 0) {
      queue[qi] = { ...entry, id: existingId };
      notify();
      return existingId;
    }
  }

  if (key) dedupeIndex.set(key, id);

  // Dialog owns presentation: never stack toasts on top; keep in queue until dialog hides.
  if (deferForDialog) {
    if (entry.priority === "high" || entry.priority === "critical" || entry.persistent) {
      queue.unshift(entry);
    } else {
      queue.push(entry);
    }
    notify();
    return id;
  }

  // Same id as visible toast → update in place (toast.promise loading→success/error).
  if (visibleId && id === visibleId) {
    showNative(entry);
    notify();
    return id;
  }

  // Same id already queued (second concurrent promise still waiting) → replace in place.
  const queuedIdx = queue.findIndex((q) => q.id === id);
  if (queuedIdx >= 0) {
    queue[queuedIdx] = entry;
    notify();
    return id;
  }

  if (!visibleId) {
    visibleId = id;
    showNative(entry);
    notify();
    return id;
  }

  // Important toast waiting while a low-priority success is showing: keep success,
  // queue important — never dismiss without id / never wipe important for success.
  if (
    entry.priority === "high" ||
    entry.priority === "critical" ||
    entry.persistent
  ) {
    queue.unshift(entry);
  } else {
    queue.push(entry);
  }
  notify();
  return id;
}

/** Call when dialog ownership ends so deferred toasts can surface. */
export function flushToastQueue() {
  if (!getOperationCoordinator().hasActiveDialogOwner()) {
    pump();
  }
}

/** Session teardown: dismiss visible toast and drop the entire queue. */
export function clearAllToasts() {
  sessionGeneration += 1;
  presentationSuspended = true;
  if (visibleId) {
    try {
      toast.dismiss(visibleId);
    } catch (_) {
      /* ignore */
    }
  }
  queue.length = 0;
  visibleId = null;
  dedupeIndex.clear();
  notify();
}

export function dismissToast(id) {
  if (!id) {
    // Spec: never use global dismiss as dedupe — require id.
    return;
  }
  toast.dismiss(id);
  if (visibleId === id) {
    visibleId = null;
    for (const [k, v] of dedupeIndex.entries()) {
      if (v === id) dedupeIndex.delete(k);
    }
    pump();
    return;
  }
  const idx = queue.findIndex((q) => q.id === id);
  if (idx >= 0) queue.splice(idx, 1);
  for (const [k, v] of dedupeIndex.entries()) {
    if (v === id) dedupeIndex.delete(k);
  }
  notify();
}

/** Called when react-hot-toast removes the visible toast (timeout / manual). */
export function onVisibleToastRemoved(id) {
  if (visibleId === id) {
    visibleId = null;
    for (const [k, v] of dedupeIndex.entries()) {
      if (v === id) dedupeIndex.delete(k);
    }
    pump();
  }
}

export const promangToast = {
  success(message, options) {
    return enqueueToast("success", message, options);
  },
  error(message, options) {
    return enqueueToast("error", message, {
      persistent: true,
      ...options,
    });
  },
  loading(message, options) {
    return enqueueToast("loading", message, options);
  },
  dismiss: dismissToast,
  /**
   * Limited promise helper that uses one toast id (update in place).
   * Prefer OperationActionDialog for multi-step work.
   * Captures session generation so resolve/reject after clearAllToasts is a
   * no-op even if presentation was resumed (login before settle).
   */
  promise(promise, messages, options = {}) {
    const gen = sessionGeneration;
    const id =
      options.id ||
      createClientOperationId({ operationKey: "toast-promise" });
    const withGen = { ...options, id, sessionGeneration: gen };
    enqueueToast("loading", messages.loading || "…", withGen);

    const resolveErrorMessage = (err) => {
      const fromHandler =
        typeof messages.error === "function"
          ? messages.error(err)
          : messages.error;
      if (fromHandler && typeof fromHandler === "string" && fromHandler.trim()) {
        return fromHandler;
      }
      if (err?.message && typeof err.message === "string" && err.message.trim()) {
        return err.message;
      }
      return "Something went wrong";
    };

    const stillCurrentSession = () => gen === sessionGeneration;

    return Promise.resolve(promise).then(
      (value) => {
        if (!stillCurrentSession()) return value;
        try {
          const msg =
            typeof messages.success === "function"
              ? messages.success(value)
              : messages.success;
          enqueueToast("success", msg, withGen);
          return value;
        } catch (err) {
          // Success-handler throw (validation / false-success): one in-place error.
          if (stillCurrentSession()) {
            enqueueToast("error", resolveErrorMessage(err), {
              ...withGen,
              persistent: true,
            });
          }
          throw err;
        }
      },
      (err) => {
        if (stillCurrentSession()) {
          enqueueToast("error", resolveErrorMessage(err), {
            ...withGen,
            persistent: true,
          });
        }
        throw err;
      }
    );
  },
};

/** Test helper */
export function __resetToastQueueForTests() {
  presentationSuspended = false;
  sessionGeneration += 1;
  queue.length = 0;
  visibleId = null;
  dedupeIndex.clear();
  notify();
}
